import { appsSdkApi } from './appsSdkApi'

export interface SdkFramework {
  id: number
  slug: string
  name: string | null
  status: number | null
  core_size_ios_bytes?: number
  core_size_android_bytes?: number
}

export interface SdkComponent {
  id: number
  framework_id: number
  capability: string | null
  description: string | null
  docs_url: string | null
  integrator: { id: number; slug: string; name: string | null }
  package_ref: string | null
  is_mediated: boolean
  size_ios_bytes: number | null
  size_android_bytes: number | null
}

export interface SdkBuild {
  // sdk_builds.id is a UUID (opaque public build id) — a string, not a number.
  id: string
  organization_id: number
  framework_id: number
  name: string | null
  label: string | null
  // The base Glaium SDK (gateway) version stamped at generation — same number the header shows as
  // "Base SDK vX". Compared to the current latest (SdkVersionInfo.version) to flag "update available".
  // null until first generated.
  version: string | null
  status: string
  size_ios_bytes: number | null
  size_android_bytes: number | null
  checksum: string | null
  artifact_ref: string | null
  // ISO-8601 last-generation time (sdk_builds.updated_at, stamped by every generate) — the "generated" date.
  updated_at?: string | null
  component_ids?: number[]
  apps?: AppConfig[]
}

export interface AdUnit {
  placement: string
  type: string // interstitial | rewarded | banner | ...
  ios: string
  android: string
}

// One in-app-purchase SKU. `productId` must match the App Store / Play product id.
// `grants` are the entitlement flags owning it unlocks (e.g. ["adFree"]) — registered in
// glaium_iap.products and read by the gateway's entitlement resolver.
export interface IapProduct {
  productId: string
  type: string // consumable | non_consumable | subscription
  name?: string
  price?: string // USD, display/analytics
  grants?: string[]
  isFullPremium?: boolean
  // Purchases of this product before `purchasedBefore` also get `countsAs`'s entitlements. Three states:
  // absent = leave any stored rule alone, null = remove it, object = set it.
  grandfather?: IapGrandfather | null
  // Where the gateway already has this product ('ios' / 'android'); set when loaded from the gateway.
  platforms?: string[]
}

export interface IapGrandfather {
  countsAs: string
  // "YYYY-MM-DD" from the form, or the ISO instant the gateway returns; null = no cutoff.
  purchasedBefore: string | null
  reason?: string
}

// Per-app SDK config (name + bundle/store ids + per-vendor app values). `vendors` is
// keyed by integrator slug; shapes vary per vendor (see PER_APP_FIELDS in the wizard).
// `iap` holds the store catalog + receipt-validation secrets (registered at generation).
export interface AppConfig {
  name?: string
  bundle?: { ios?: string; android?: string }
  store?: { ios?: string; android?: string }
  // Shown in the GDPR consent flow (MAX / Google UMP); required for MAX apps.
  privacy?: { privacyPolicyUrl?: string; termsOfServiceUrl?: string; attUsageDescription?: string }
  vendors?: Record<string, any>
  // Android receipt validation uses ONE shared Glaium service account (customers invite it into
  // their Play Console) — no per-app key here. Apple shared secret is per-app (subscriptions only).
  iap?: { products?: IapProduct[]; appleSharedSecret?: string }
}

export interface VendorIntegration {
  id: number
  name: string
  fields_set: boolean
}

export interface CredentialFieldDef {
  name: string
  label: string | null
  tip: string | null
  input_type: string | null
}

export interface VendorCredStatus {
  integrator: { id: number; slug: string; name: string | null }
  required: boolean
  kind?: 'account' | 'per_app' | 'managed' | 'none'
  integrations?: VendorIntegration[]
  selected_integration_id?: number | null
  fields_set?: boolean
  field_defs?: CredentialFieldDef[]
  app_ready?: boolean
  missing_keys?: string[]
}

export interface CredentialsStatus {
  build_id: string
  organization_id: number
  ready: boolean
  vendors: VendorCredStatus[]
}

export interface GenerateResult {
  build_id: string
  status: string
  version: string
  filename: string
  checksum: string
  size_ios_bytes: number
  size_android_bytes: number
  artifact_bytes: number
  files: string[]
  capabilities: string[]
  credentials_incomplete: { integrator: string; reason: string }[]
}

// The repo-level base Glaium SDK version published by an apps-sdk deploy (bumps every deploy):
// its version + publish date. `version` is null when the marker is briefly unreadable — the header
// just omits the line in that case.
export interface SdkVersionInfo {
  version: string | null // the base Glaium SDK version — header line + what builds are stamped with
  published_at: string | null // ISO-8601
  error: string | null
}

// filename from a Content-Disposition header, preferring RFC 5987 filename*= when present.
// Returns null rather than a guess, so the caller decides what to do without a server answer.
function parseContentDispositionFilename(header?: string | null): string | null {
  if (!header) return null
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (star?.[1]) { try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')) } catch { /* fall through */ } }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || null
}

export const appsSdkService = {
  health: () => appsSdkApi.get('/health').then((r) => r.data),

  getSdkVersion: () => appsSdkApi.get('/sdk-version').then((r) => r.data as SdkVersionInfo),

  getCatalog: (framework?: string) =>
    appsSdkApi
      .get('/catalog', { params: framework ? { framework } : {} })
      .then((r) => r.data as { frameworks: SdkFramework[]; components: SdkComponent[] }),

  // Lightweight — frameworks only (no vendor components). The list + framework picker use this;
  // components are fetched per-framework via getCatalog(slug) when a build is opened/created.
  getFrameworks: () => appsSdkApi.get('/frameworks').then((r) => r.data.frameworks as SdkFramework[]),

  listBuilds: (organizationId?: number) =>
    appsSdkApi
      .get('/builds', { params: organizationId ? { organization_id: organizationId } : {} })
      .then((r) => r.data.builds as SdkBuild[]),

  getBuild: (id: string) => appsSdkApi.get(`/builds/${id}`).then((r) => r.data as SdkBuild),

  deleteBuild: (id: string) => appsSdkApi.delete(`/builds/${id}`).then((r) => r.data),

  createBuild: (body: { organization_id: number; framework: string; name?: string; label?: string }) =>
    appsSdkApi.post('/builds', body).then((r) => r.data as SdkBuild),

  setComponents: (id: string, componentIds: number[]) =>
    appsSdkApi.post(`/builds/${id}/components`, { component_ids: componentIds }).then((r) => r.data),

  getSize: (id: string) =>
    appsSdkApi
      .get(`/builds/${id}/size`)
      .then((r) => r.data as { build_id: string; component_count: number; size_ios_bytes: number; size_android_bytes: number }),

  // The app's catalog as the gateway already holds it (glaium_iap.products), keyed by app name like provisioning.
  getGatewayProducts: (name: string) =>
    appsSdkApi
      .get('/builds/apps/gateway-products', { params: { name } })
      .then((r) => r.data as { available: boolean; products: IapProduct[] }),

  getCredentialsStatus: (id: string) =>
    appsSdkApi.get(`/builds/${id}/credentials-status`).then((r) => r.data as CredentialsStatus),

  setIntegrationSelection: (buildId: string, integratorId: number, integrationId: number | null) =>
    appsSdkApi
      .put(`/builds/${buildId}/integration-selection`, { integrator_id: integratorId, integration_id: integrationId })
      .then((r) => r.data),

  setAppConfig: (buildId: string, apps: AppConfig[]) =>
    appsSdkApi.put(`/builds/${buildId}/app-config`, { apps }).then((r) => r.data),

  generate: (id: string) => appsSdkApi.post(`/builds/${id}/generate`).then((r) => r.data as GenerateResult),

  // The artifact endpoint is JWT-protected, so a plain browser navigation (a bare
  // <a href>) 401s. Fetch it through the authenticated client as a blob instead.
  // Returns the server's filename alongside the bytes. The extension is NOT ours to guess: a
  // Unity bundle is a real zip and an RN/Flutter one is a gzip tarball, and this call used to
  // discard the headers so the caller appended ".tgz" to both. A zip named .tgz cannot be opened
  // by unzip OR tar — you have to run `file` on it to find out what you have.
  downloadArtifact: (id: string): Promise<{ blob: Blob; filename: string | null }> =>
    appsSdkApi.get(`/builds/${id}/artifact`, { responseType: 'blob' }).then((r) => ({
      blob: r.data as Blob,
      filename: parseContentDispositionFilename(r.headers?.['content-disposition']),
    })),
}
